import {
  ContentSwitcher,
  IconButton,
  OpenInProductIcon,
  TextInput,
  type ContentSwitcherOption,
  type ListViewStatus,
} from "@carbon6/c6-frontend-design";
import { useMemo, useState, type ChangeEvent, type ReactNode } from "react";
import type { TrainingPolicyView } from "@/api";
import { DatasetPreview } from "@/components/dataset";
import { BranchHashCell, shortSha } from "@/components/table/branchHash";
import { HatListView } from "@/components/table/HatListView";
import { policyDatasetLabel } from "@/page/training/trainingPolicyFormat";
import { sessionReference, TrainingSessionRefLink } from "@/page/training/TrainingSessionRefLink";
import { openTrainingSessionWindow } from "@/page/training/trainingSessionWindow";
import { formatDateTime, parseUtcMs } from "@/utils/datetime";
import { initStrategyLockReason } from "./submitWizardFeatures";
import type { SubmitInitState } from "./submitWizardForm";
import type { SubmitWizardController } from "./useSubmitWizardController";
import styles from "./submitWizard.module.css";

// 무엇을 시작점으로 쓰는지 한글로 표시하고 BE 어휘를 괄호로 함께 적는다. 연구자가 상류 UI·문서
// 에서 보는 말과 연결되어야 하기 때문이다. 표면은 세 축뿐이다. BE 는 checkpoint_uri 직접 지정과
// session kind 를 열지 않는다.
//
// **세로 목록이다**(사용자 확정). 가로 세그먼트에서는 세 라벨의 길이가 달라서, 균등 분할하면 긴
// 라벨이 자기 칸을 넘고 차이를 설명할 자리도 없다. 세로 목록에서는 항목마다 한 줄 설명을 붙이고,
// 고를 수 없는 항목은 사유를 툴팁이 아니라 그 자리에 표시한다.
type InitStrategyChoice = {
  value: SubmitInitState["strategy"];
  label: string;
  hint: string;
};

const STRATEGY_CHOICES: readonly InitStrategyChoice[] = [
  {
    value: "scratch",
    label: "처음부터 (scratch)",
    hint: "Weights 를 처음부터 학습합니다.",
  },
  {
    value: "finetune",
    label: "Policy에서 (fine tune)",
    hint: "등록된 Policy의 Weights 를 시작점으로 씁니다.",
  },
  {
    value: "retrain",
    label: "Checkpoint 이어서 (retrain)",
    hint: "고른 Session의 마지막 Checkpoint에서 다시 학습합니다.",
  },
];

const RETRAIN_VALUE = "retrain";
// 소스는 진입점(세션 상세의 [재학습])만 정할 수 있다. 위저드에는 세션을 고르는 목록이 없다.
const RETRAIN_DISABLED_HINT = "Training Session 상세에서 [재학습]을 눌러 시작해 주세요.";

function strategyOf(value: string): SubmitInitState["strategy"] {
  if (value === "finetune" || value === RETRAIN_VALUE) {
    return value;
  }
  return "scratch";
}

const SCOPE_OPTIONS: readonly ContentSwitcherOption[] = [
  { value: "me", label: "내 Policy" },
  { value: "team", label: "팀 Policy" },
];

// Policy 출처 스위처는 **내용 폭**이다. 열을 채우면(expanded) 세그먼트를 균등하게 나눈다. c6 는
// 그 모드에서 라벨을 자르지 않아서 긴 라벨이 자기 칸을 넘는다(RFC 011).
const SWITCHER_WIDTH = { type: "intrinsic" } as const;

// 로드 전·실패 때의 빈 목록. 렌더마다 새 배열을 만들면 검색 memo 가 계속 무효화된다.
const EMPTY_POLICIES: readonly TrainingPolicyView[] = [];

type InitStrategyPanelProps = { controller: SubmitWizardController };

// 학습 시작점(init) 선택. finetune 은 resources 를 1×1 로 잠그고 config 를 finetune 기본
// 레시피로 바꾼다. 그래서 그 영향을 먼저 경고로 표시한다.
export function InitStrategyPanel({ controller }: InitStrategyPanelProps): ReactNode {
  const {
    form,
    features,
    initPolicyScope,
    initPolicyState,
    reservedInitOverrides,
    finetuneConfigNotice,
    retrainSource,
    retrainConfigNotice,
    setInitStrategy,
    setInitPolicy,
    setInitPolicyScope,
  } = controller;
  const [query, setQuery] = useState("");
  const init = form.init;
  const finetune = init.strategy === "finetune";

  const policies = useMemo(
    () => (initPolicyState.status === "success" ? initPolicyState.data : EMPTY_POLICIES),
    [initPolicyState],
  );
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (needle === "") {
      return policies;
    }
    return policies.filter((policy) => policyHaystack(policy).includes(needle));
  }, [policies, query]);

  return (
    <section className={styles.initPanel} aria-label="init">
      {/* 보이는 제목은 두지 않는다. 섹션 내비와 필드 라벨이 제목을 소유한다. aria-label 은
          스크린리더용으로 남긴다. */}
      <div className={styles.initChoices} role="radiogroup" aria-label="init strategy">
        {STRATEGY_CHOICES.map((choice) => {
          // 잠기는 이유는 두 가지다.
          //   - 이 위저드가 열지 않는 축. 기능 표가 사유도 준다.
          //   - retrain 처럼 소스가 있어야 고를 수 있는 축. 폼 상태이므로 이 컴포넌트가 사유를 안다.
          const outOfScopeReason = initStrategyLockReason(features, choice.value);
          const needsRetrainSource = choice.value === RETRAIN_VALUE && retrainSource === null;
          const locked = outOfScopeReason !== null || needsRetrainSource;
          const lockedReason =
            outOfScopeReason ?? (needsRetrainSource ? RETRAIN_DISABLED_HINT : null);
          return (
            <label
              key={choice.value}
              className={styles.initChoice}
              data-selected={init.strategy === choice.value ? "true" : "false"}
              data-locked={locked ? "true" : "false"}
            >
              <input
                type="radio"
                name="submit-init-strategy"
                value={choice.value}
                checked={init.strategy === choice.value}
                disabled={locked}
                onChange={() => setInitStrategy(strategyOf(choice.value))}
              />
              {/* 라벨은 c6 Text 가 아니라 평문 span 이다. lint 는 컴포넌트 안쪽의 label 접근
                  이름을 검사하지 못한다. 타이포는 CSS 한 줄로 같은 토큰에 맞출 수 있다. */}
              <span className={styles.initChoiceText}>
                <span className={styles.initChoiceLabel}>{choice.label}</span>
                <span className={styles.initChoiceHint}>{lockedReason ?? choice.hint}</span>
              </span>
            </label>
          );
        })}
      </div>
      {init.strategy === "retrain" ? (
        <>
          {/* 재학습은 소스 세션의 마지막 체크포인트에서 이어 학습하지만, 새 세션과 새 곡선을
              만든다. 상세의 [이어서 학습](resume)과 달리 부모 run 을 잇지 않는다. */}
          <p className={styles.initWarning}>
            이 Session의 마지막 Checkpoint에서 다시 학습합니다. 자원은 1노드×1GPU 로 고정됩니다.
          </p>
          {retrainConfigNotice === null ? null : (
            <p className={styles.initWarning}>{retrainConfigNotice}</p>
          )}
          {reservedInitOverrides.length === 0 ? null : (
            <p className={styles.initWarning}>
              재학습에서는 플랫폼이 채우는 키라 직접 지정할 수 없습니다. 아래 override 를 지워
              주세요: {reservedInitOverrides.join(", ")}
            </p>
          )}
          <div className={styles.retrainSourceCard}>
            <span className={styles.retrainSourceLabel}>시작점 Session</span>
            <TrainingSessionRefLink
              sessionId={init.source.id}
              displayName={init.source.title}
              className={styles.policyName}
            />
          </div>
        </>
      ) : null}
      {finetune && init.strategy === "finetune" ? (
        <>
          {init.policy === null ? (
            <p className={styles.initWarning}>시작점으로 쓸 Policy를 골라 주세요.</p>
          ) : null}
          {finetuneConfigNotice === null ? null : (
            <p className={styles.initWarning}>{finetuneConfigNotice}</p>
          )}
          {reservedInitOverrides.length === 0 ? null : (
            <p className={styles.initWarning}>
              finetune 에서는 플랫폼이 채우는 키라 직접 지정할 수 없습니다. 아래 override 를 지워
              주세요: {reservedInitOverrides.join(", ")}
            </p>
          )}
          <ContentSwitcher
            size="md"
            width={SWITCHER_WIDTH}
            label="Policy 스코프"
            options={SCOPE_OPTIONS}
            value={initPolicyScope}
            onValueChange={(value) => setInitPolicyScope(value === "team" ? "team" : "me")}
          />
          <TextInput
            value={query}
            placeholder="Policy·Session·실험 이름으로 좁히기"
            aria-label="Policy 검색"
            onChange={(event: ChangeEvent<HTMLInputElement>) => setQuery(event.target.value)}
          />
          <HatListView
            variant="compact"
            className={styles.initPolicyList}
            ariaLabel="init policy"
            items={filtered}
            getKey={(policy) => policy.id}
            selectedKey={init.policy?.id ?? null}
            // 출처(브랜치·커밋)를 함께 전달한다. "이 Commit 으로 맞추기" 제안이 이 값을 사용한다.
            onSelect={(policy) =>
              setInitPolicy({
                id: policy.id,
                name: policy.name,
                learningCoreRef: policy.learning_core_ref,
                learningCoreSha: policy.learning_core_sha,
              })
            }
            status={listStatus(initPolicyState.status)}
            errorMessage={initPolicyState.status === "error" ? initPolicyState.message : null}
            emptyMessage={
              policies.length === 0
                ? "이 스코프에 등록된 Policy가 없습니다."
                : "검색과 일치하는 Policy가 없습니다."
            }
            // 코드 복사와 상세 창 열기는 행 선택과 대상이 달라서 c6 액션 셀에 둔다.
            // content 안에 두면 선택 오버레이가 덮어서 클릭도 스크린리더도 접근하지 못한다.
            renderItem={(policy) => ({
              content: <PolicyCard policy={policy} />,
              actions: (
                <div className={styles.policyActions}>
                  <dl className={styles.policyFacts}>
                    <PolicyFact
                      label="Branch"
                      value={
                        <BranchHashCell
                          branch={policy.learning_core_ref}
                          hash={shortSha(policy.learning_core_sha)}
                          fullHash={policy.learning_core_sha}
                        />
                      }
                    />
                  </dl>
                  <IconButton
                    size="sm"
                    variant="ghost"
                    border={false}
                    aria-label={`${policySessionLabel(policy)} 상세 창 열기`}
                    onClick={() =>
                      openTrainingSessionWindow(
                        sessionReference(policy.source_session_id, policySessionLabel(policy)),
                      )
                    }
                  >
                    <OpenInProductIcon />
                  </IconButton>
                </div>
              ),
            })}
          />
        </>
      ) : null}
    </section>
  );
}

// 시작점 후보 카드 한 장. 무엇을 이어받을지 고르려면 이름만으로는 부족하다. 실행자, 데이터,
// 코드가 같은 이름의 정책들을 구분한다.
//
// 카드 본문은 시작점 선택을 맡고, 코드 복사와 세션 상세 열기는 액션 영역이 맡는다.
function PolicyCard({ policy }: { policy: TrainingPolicyView }): ReactNode {
  const dataset = policy.dataset_registry_ref;
  return (
    <div className={styles.policyCard}>
      {/* 학습 데이터가 시작점을 고르는 가장 큰 기준이라 데이터셋 섬네일을 앞에 둔다.
          Policy 응답이 서명 URL 을 함께 주므로 추가 요청이 없다(없으면 이니셜 fallback). */}
      <DatasetPreview
        label={dataset?.display_name ?? policy.name}
        imageUrl={dataset?.thumbnail_url ?? null}
        mode="table"
        className={styles.policyThumb}
      />
      <div className={styles.policyBody}>
        <span className={styles.policyName}>{policy.name}</span>
        <dl className={styles.policyFacts}>
          <PolicyFact label="날짜" value={formatDateTime(parseUtcMs(policy.created_at))} />
          <PolicyFact label="멤버" value={policy.owner_member?.display_name ?? null} />
          <PolicyFact label="Dataset" value={policyDatasetLabel(policy)} />
          {/* 실험과 세션은 같은 계보의 두 층이라 한 줄에 둔다. 여는 동작은 행 오른쪽 끝 액션이
              가진다. 카드 본문 전체가 선택 대상이라 본문 안에 두면 누를 수 없다. */}
          <div className={styles.policyFact}>
            <dt className={styles.policyFactLabel}>Training Session</dt>
            <dd className={styles.policyFactValue}>
              {policy.experiment_name === null ? null : (
                <span className={styles.policyExperiment}>{policy.experiment_name} · </span>
              )}
              {policySessionLabel(policy)}
            </dd>
          </div>
        </dl>
      </div>
    </div>
  );
}

// 세션 표시 이름. 이름이 없으면 id 를 쓴다. 카드 본문 문구와 액션의 접근성 이름이 같은
// 문자열이어야 "이 아이콘이 어느 세션을 여는지"가 일치한다.
function policySessionLabel(policy: TrainingPolicyView): string {
  return policy.session_display_name ?? policy.source_session_id;
}

function PolicyFact({ label, value }: { label: string; value: ReactNode }): ReactNode {
  if (value === null || value === "") {
    return null;
  }
  return (
    <div className={styles.policyFact}>
      <dt className={styles.policyFactLabel}>{label}</dt>
      <dd className={styles.policyFactValue}>{value}</dd>
    </div>
  );
}

// 검색 대상. 이름만으로는 같은 이름의 정책을 구분할 수 없어서 lineage 도 함께 검색한다.
function policyHaystack(policy: TrainingPolicyView): string {
  return [policy.name, policy.session_display_name, policy.experiment_name]
    .filter((part): part is string => part !== null)
    .join(" ")
    .toLowerCase();
}

function listStatus(status: "idle" | "loading" | "success" | "error"): ListViewStatus {
  return status;
}
